import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showSuccess, showError } from '../utils/alerts';
import Modal from './Modal';
import { ItemCreateData } from '../types/item';
import { itemService } from '../services/itemService';

interface AddItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  item?: any; // For edit mode
}

const validationSchema = yup.object({
  type: yup.string().oneOf(['PRODUCTO', 'SERVICIO']).required('Tipo de artículo es requerido'),
  name: yup.string()
    .min(2, 'El nombre debe tener al menos 2 caracteres')
    .max(255, 'El nombre no puede exceder 255 caracteres')
    .required('Nombre es requerido'),
  code: yup.string()
    .max(50, 'El código no puede exceder 50 caracteres')
    .optional(),
  description: yup.string()
    .max(1000, 'La descripción no puede exceder 1000 caracteres')
    .optional(),
  price: yup.number()
    .min(0, 'El precio debe ser mayor o igual a 0')
    .required('Precio es requerido'),
  cost_price: yup.number()
    .min(0, 'El precio costo debe ser mayor o igual a 0')
    .required('Precio costo es requerido'),
  tax_rate: yup.number()
    .min(0, 'La tasa de impuesto debe ser mayor o igual a 0')
    .max(100, 'La tasa de impuesto no puede exceder 100%')
    .required('Tasa de impuesto es requerida'),
  active: yup.boolean().optional()
});

const AddItemModal: React.FC<AddItemModalProps> = ({ isOpen, onClose, item }) => {
  const queryClient = useQueryClient();
  const [autoGenerateCode, setAutoGenerateCode] = useState(false);
  const isEditMode = !!item;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue
  } = useForm<ItemCreateData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      type: item?.type || 'PRODUCTO',
      name: item?.name || '',
      code: item?.code || '',
      description: item?.description || '',
      price: item?.price || 0,
      cost_price: item?.cost_price || 0,
      tax_rate: item?.tax_rate || 0,
      active: item?.active ?? true
    }
  });

  // Reset form when item changes (for edit mode)
  useEffect(() => {
    if (item) {
      reset({
        type: item.type || 'PRODUCTO',
        name: item.name || '',
        code: item.code || '',
        description: item.description || '',
        price: item.price || 0,
        cost_price: item.cost_price || 0,
        tax_rate: item.tax_rate || 0,
        active: item.active ?? true
      });
    }
  }, [item, reset]);

  const watchedName = watch('name');
  const watchedType = watch('type');

  // Auto-generate code when name or type changes and auto-generate is enabled
  React.useEffect(() => {
    if (autoGenerateCode && watchedName) {
      const generatedCode = itemService.generateDefaultCode(watchedName, watchedType);
      setValue('code', generatedCode);
    }
  }, [watchedName, watchedType, autoGenerateCode, setValue]);

  const createItemMutation = useMutation({
    mutationFn: itemService.createItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      showSuccess('Artículo creado exitosamente');
      reset();
      setAutoGenerateCode(false);
      onClose();
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Error al crear el artículo';
      showError(message);
    }
  });

  const updateItemMutation = useMutation({
    mutationFn: (data: { id: number; itemData: Partial<ItemCreateData> }) =>
      itemService.updateItem(data.id, data.itemData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      showSuccess('Artículo actualizado exitosamente');
      reset();
      setAutoGenerateCode(false);
      onClose();
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Error al actualizar el artículo';
      showError(message);
    }
  });

  const onSubmit = async (data: ItemCreateData) => {
    try {
      // Remove empty code if not provided
      if (!data.code?.trim()) {
        delete data.code;
      }

      if (isEditMode && item) {
        await updateItemMutation.mutateAsync({
          id: item.id,
          itemData: data
        });
      } else {
        await createItemMutation.mutateAsync(data);
      }
    } catch (error) {
      // Error is handled in the mutation onError callback
    }
  };

  const handleClose = () => {
    reset();
    setAutoGenerateCode(false);
    onClose();
  };


  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={isEditMode ? "Editar Artículo" : "Agregar Artículo"} size="lg">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Tipo de Artículo */}
          <div>
            <label className="form-label">
              Tipo de Artículo <span className="text-red-500">*</span>
            </label>
            <select {...register('type')} className="form-select">
              <option value="PRODUCTO">Producto</option>
              <option value="SERVICIO">Servicio</option>
            </select>
            {errors.type && (
              <p className="form-error">{errors.type.message}</p>
            )}
          </div>

          {/* Código */}
          <div>
            <label className="form-label">Código</label>
            <div className="space-y-2">
              <input
                type="text"
                {...register('code')}
                className="form-input"
                placeholder="SKU, código interno..."
                disabled={autoGenerateCode}
              />
              <label className="flex items-center text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={autoGenerateCode}
                  onChange={(e) => setAutoGenerateCode(e.target.checked)}
                  className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Generar código automáticamente
              </label>
            </div>
            {errors.code && (
              <p className="form-error">{errors.code.message}</p>
            )}
          </div>
        </div>

        {/* Nombre */}
        <div>
          <label className="form-label">
            Nombre del Artículo <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            {...register('name')}
            className="form-input"
            placeholder="Nombre descriptivo del artículo"
          />
          {errors.name && (
            <p className="form-error">{errors.name.message}</p>
          )}
        </div>

        {/* Descripción */}
        <div>
          <label className="form-label">Descripción</label>
          <textarea
            {...register('description')}
            rows={3}
            className="form-textarea"
            placeholder="Descripción detallada del artículo"
          />
          {errors.description && (
            <p className="form-error">{errors.description.message}</p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Precio */}
          <div>
            <label className="form-label">
              Precio <span className="text-red-500">*</span>
            </label>
            <div className="flex">
              <span className="inline-flex items-center px-3 text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-md">
                Q
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                {...register('price', {
                  setValueAs: (value) => parseFloat(value) || 0
                })}
                className="form-input rounded-l-none border-l-0"
                placeholder="0.00"
              />
            </div>
            {errors.price && (
              <p className="form-error">{errors.price.message}</p>
            )}
          </div>

          {/* Precio Costo */}
          <div>
            <label className="form-label">
              Precio Costo <span className="text-red-500">*</span>
            </label>
            <div className="flex">
              <span className="inline-flex items-center px-3 text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-md">
                Q
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                {...register('cost_price', {
                  setValueAs: (value) => parseFloat(value) || 0
                })}
                className="form-input rounded-l-none border-l-0"
                placeholder="0.00"
              />
            </div>
            {errors.cost_price && (
              <p className="form-error">{errors.cost_price.message}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Tasa de Impuesto */}
          <div>
            <label className="form-label">
              Tasa de Impuesto (%) <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                {...register('tax_rate', {
                  setValueAs: (value) => parseFloat(value) || 0
                })}
                className="form-input pr-8"
                placeholder="0.00"
              />
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                %
              </span>
            </div>
            {errors.tax_rate && (
              <p className="form-error">{errors.tax_rate.message}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Ejemplo: 12 para IVA del 12%
            </p>
          </div>

          {/* Espacio vacío para mantener el layout */}
          <div></div>
        </div>

        {/* Estado Activo */}
        <div className="flex items-center">
          <input
            type="checkbox"
            {...register('active')}
            id="active"
            className="mr-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="active" className="text-sm text-gray-700">
            Artículo activo (disponible para ventas)
          </label>
        </div>

        {/* Vista previa de cálculos */}
        {(watch('price') > 0 || watch('cost_price') > 0) && (
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <h4 className="font-medium text-gray-900">Vista previa de precios:</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Precio costo:</span>
                <span className="float-right font-medium">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'GTQ',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }).format(watch('cost_price') || 0)}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Precio venta:</span>
                <span className="float-right font-medium">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'GTQ',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }).format(watch('price') || 0)}
                </span>
              </div>
              {(watch('price') > 0 || watch('cost_price') > 0) && (
                <>
                  <div>
                    <span className="text-gray-600">Margen bruto:</span>
                    <span className={`float-right font-medium ${
                      ((watch('price') || 0) - (watch('cost_price') || 0)) >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'GTQ',
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      }).format((watch('price') || 0) - (watch('cost_price') || 0))}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Margen (%):</span>
                    <span className={`float-right font-medium ${
                      ((watch('price') || 0) > 0 && (watch('cost_price') || 0) > 0) ?
                        (((watch('price') || 0) - (watch('cost_price') || 0)) / (watch('cost_price') || 1) * 100 >= 0 ? 'text-green-600' : 'text-red-600')
                        : 'text-gray-600'
                    }`}>
                      {((watch('price') || 0) > 0 && (watch('cost_price') || 0) > 0) ?
                        `${(((watch('price') || 0) - (watch('cost_price') || 0)) / (watch('cost_price') || 1) * 100).toFixed(1)}%`
                        : '0.0%'
                      }
                    </span>
                  </div>
                </>
              )}
              <div className="col-span-2 border-t pt-2">
                <span className="text-gray-600">Impuesto ({watch('tax_rate')}%):</span>
                <span className="float-right font-medium">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'GTQ',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }).format((watch('price') || 0) * (watch('tax_rate') || 0) / 100)}
                </span>
              </div>
              <div className="col-span-2 border-t pt-2">
                <span className="text-gray-900 font-medium">Precio total con IVA:</span>
                <span className="float-right font-bold text-green-600">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'GTQ',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }).format((watch('price') || 0) * (1 + (watch('tax_rate') || 0) / 100))}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Botones */}
        <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            className="btn-secondary order-2 sm:order-1"
            disabled={isSubmitting}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn-primary order-1 sm:order-2"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? (isEditMode ? 'Actualizando...' : 'Creando...')
              : (isEditMode ? 'Actualizar Artículo' : 'Crear Artículo')
            }
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddItemModal;